import { QueryClient, useMutation, useQuery, useQueryClient } from "react-query";

import { api } from "../api";
import { Page, pageQueryParams, unwrapListResponse } from "../pagination";
import { ApiKeySummary, IssuedApiKey } from "./types";

export enum ApiKeyQueryKeys {
  OrganisationApiKeys = "integrationApiKeys",
}

const ROUTE = "integration/v1/api-keys";

export const ISSUED_API_KEY_MUTATION_KEY = ["integrationApiKeys", "issued"] as const;

export const useOrganisationApiKeys = (organisationId?: string) =>
  // Keyed on the organisation id so an in-SPA org switch never renders the previous org's keys
  // before its own refetch resolves. The server derives the org from the JWT; the id is a cache
  // key only. Same audit-freshness overrides as the connected-apps hook: a key created or revoked
  // in another tab must surface on revisit/refocus.
  useQuery<ApiKeySummary[], Error>(
    [ApiKeyQueryKeys.OrganisationApiKeys, organisationId],
    // Asks the server for one page, as every list must. One page of 50 always holds the ten-key cap, and
    // unwrapListResponse accepts either shape: the paged envelope, or the legacy array while the API's
    // ORG_SCALE_PAGINATION flag is off.
    async () =>
      unwrapListResponse(
        await api.get<ApiKeySummary[] | Page<ApiKeySummary>>(
          ROUTE,
          pageQueryParams({ limit: 50, offset: 0 }),
        ),
      ).items,
    {
      enabled: Boolean(organisationId),
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  );

const useInvalidateApiKeys = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries(ApiKeyQueryKeys.OrganisationApiKeys);
};

export const useCreateApiKey = () => {
  const invalidate = useInvalidateApiKeys();
  return useMutation<IssuedApiKey, Error, { name: string }>((body) => api.post<IssuedApiKey>(ROUTE, body), {
    mutationKey: [...ISSUED_API_KEY_MUTATION_KEY, "create"],
    onSettled: invalidate,
  });
};

export const useRotateApiKey = () => {
  const invalidate = useInvalidateApiKeys();
  return useMutation<IssuedApiKey, Error, string>((id) => api.post<IssuedApiKey>(`${ROUTE}/${id}/rotate`), {
    mutationKey: [...ISSUED_API_KEY_MUTATION_KEY, "rotate"],
    onSettled: invalidate,
  });
};

export const useRevokeApiKey = () => {
  const invalidate = useInvalidateApiKeys();
  // Invalidate on settle (success AND error): a revoke whose response was lost, or a concurrent
  // revoke that 404s, must still reconcile the list.
  return useMutation<unknown, Error, string>((id) => api.del(`${ROUTE}/${id}`), {
    onSettled: invalidate,
  });
};

/**
 * Drops every create/rotate mutation from react-query's MutationCache so the plaintext key it
 * returned does not sit in memory for the rest of the session. Call it when the user has
 * confirmed they stored the key, and when the tile that issued it unmounts. Mutations have no gc in react-query v3, so this is the only way out.
 */
export const forgetIssuedApiKeys = (queryClient: QueryClient): void => {
  const cache = queryClient.getMutationCache();
  cache
    .getAll()
    .filter((mutation) => {
      const key = mutation.options.mutationKey;
      return (
        Array.isArray(key) &&
        key[0] === ISSUED_API_KEY_MUTATION_KEY[0] &&
        key[1] === ISSUED_API_KEY_MUTATION_KEY[1]
      );
    })
    .forEach((mutation) => cache.remove(mutation));
};
